/**
 * Invoice Detail Component
 * Full invoice view with actions (send, print, download, void, record payment).
 * Includes email tracking panel and payment history.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  ArrowLeft, Send, Download, Printer, Copy, DollarSign,
  XCircle, RefreshCw, CheckCircle, AlertTriangle, Eye, Mail,
  Clock, Link, ExternalLink
} from 'lucide-react';
import {
  invoiceAPI,
  Invoice,
  INVOICE_STATUS_LABELS,
  formatCents,
  centsToDecimal,
  decimalToCents,
  PaymentMethod,
  PAYMENT_METHOD_LABELS,
} from '@/services/api/invoiceAPI';
import { InvoicePreview } from './InvoicePreview';
import { SendInvoiceModal } from './SendInvoiceModal';
import { useRevOps } from '@/contexts/RevOpsContext';

interface InvoiceDetailProps {
  organizationId: number;
  organizationName: string;
  invoiceId: number;
  onBack: () => void;
  onEdit: (invoiceId: number) => void;
  onDuplicate?: (invoiceId: number) => void;
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  open: 'bg-blue-100 text-blue-700',
  sent: 'bg-yellow-100 text-yellow-700',
  viewed: 'bg-purple-100 text-purple-700',
  overdue: 'bg-red-100 text-red-700',
  paid: 'bg-green-100 text-green-700',
  void: 'bg-slate-100 text-slate-700',
  uncollectible: 'bg-orange-100 text-orange-700',
};

export function InvoiceDetail({
  organizationId,
  organizationName,
  invoiceId,
  onBack,
  onEdit,
  onDuplicate,
}: InvoiceDetailProps) {
  const { fmt } = useFormattedDate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Template for draft branding
  const { invoiceTemplates } = useRevOps();
  const defaultTemplate = invoiceTemplates.find(t => t.isDefault) || invoiceTemplates[0] || null;

  // Payment form
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod>('other');
  const [payReference, setPayReference] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [payingAction, setPayingAction] = useState(false);

  const fetchInvoice = useCallback(async () => {
    try {
      const data = await invoiceAPI.getInvoice(organizationId, invoiceId);
      setInvoice(data);
    } catch (err) {
      setError('Failed to load invoice');
    }
  }, [organizationId, invoiceId]);

  useEffect(() => {
    setLoading(true);
    fetchInvoice().finally(() => setLoading(false));
  }, [fetchInvoice]);

  const handleFinalize = async () => {
    if (!invoice) return;
    try {
      const updated = await invoiceAPI.finalizeInvoice(organizationId, invoice.id);
      setInvoice(prev => prev ? { ...prev, ...updated } : null);
      setSuccess('Invoice finalized');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to finalize');
    }
  };

  const handleVoid = async () => {
    if (!invoice || !confirm(`Void invoice ${invoice.invoice_number}?`)) return;
    try {
      await invoiceAPI.voidInvoice(organizationId, invoice.id, 'Voided by user');
      await fetchInvoice();
      setSuccess('Invoice voided');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to void');
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    try {
      await invoiceAPI.openPdfInNewTab(organizationId, invoice.id);
    } catch (err) {
      setError('Failed to generate PDF');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopyPortalLink = async () => {
    if (!invoice) return;
    try {
      let portalUrl = invoice.portal?.url;
      if (!portalUrl) {
        const result = await invoiceAPI.generatePortalLink(organizationId, invoice.id);
        portalUrl = result.portal_url;
        // Refresh invoice to show portal info in sidebar
        await fetchInvoice();
      }
      const fullUrl = `${window.location.origin}${portalUrl}`;
      await navigator.clipboard.writeText(fullUrl);
      setSuccess('Portal link copied to clipboard');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to generate portal link');
    }
  };

  const handleRecordPayment = async () => {
    if (!invoice || !payAmount) return;
    setPayingAction(true);
    try {
      await invoiceAPI.recordPayment(organizationId, invoice.id, {
        amount_cents: decimalToCents(parseFloat(payAmount)),
        payment_method: payMethod,
        reference_number: payReference || undefined,
        notes: payNotes || undefined,
      });
      await fetchInvoice();
      setShowPaymentForm(false);
      setPayAmount('');
      setPayReference('');
      setPayNotes('');
      setSuccess('Payment recorded');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPayingAction(false);
    }
  };

  const handleEmailSent = () => {
    setShowSendModal(false);
    fetchInvoice();
    setSuccess('Invoice email sent');
    setTimeout(() => setSuccess(null), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Loading invoice...</span>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Invoice not found</p>
        <button onClick={onBack} className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium">Go back</button>
      </div>
    );
  }

  // For finalized invoices, use frozen branding_snapshot; for drafts, use template branding
  const rawBranding = (invoice as any).branding_snapshot;
  // branding_snapshot defaults to {} in the DB — treat empty objects as "no branding stored"
  const storedBranding = rawBranding && typeof rawBranding === 'object' && Object.keys(rawBranding).length > 0
    ? rawBranding : undefined;
  const templateBranding = defaultTemplate ? {
    logo_url: defaultTemplate.showLogo ? defaultTemplate.logoUrl : undefined,
    primary_color: defaultTemplate.primaryColor,
    company_name: defaultTemplate.companyName,
  } : undefined;

  const previewData = {
    invoice_number: invoice.invoice_number,
    customer_name: invoice.customer_snapshot?.name || 'Customer',
    customer_company: invoice.customer_snapshot?.company,
    customer_email: invoice.customer_snapshot?.email,
    customer_address: [
      invoice.customer_snapshot?.address_line1,
      [invoice.customer_snapshot?.city, invoice.customer_snapshot?.state, invoice.customer_snapshot?.postal_code].filter(Boolean).join(', '),
    ].filter(Boolean).join('\n'),
    invoice_date: invoice.invoice_date,
    due_date: invoice.due_date,
    payment_terms: invoice.payment_terms,
    line_items: (invoice.line_items || []).map(li => {
      // Extract item_name from metadata or split "ItemName - Description" for backward compat
      let itemName = li.metadata?.item_name || undefined;
      let desc = li.description;
      if (!itemName && desc.includes(' - ')) {
        const dashIdx = desc.indexOf(' - ');
        itemName = desc.substring(0, dashIdx);
        desc = desc.substring(dashIdx + 3);
      }
      return {
        item_name: itemName,
        description: desc,
        quantity: li.quantity,
        unit_price: centsToDecimal(li.unit_price_cents),
        amount: centsToDecimal(li.amount_cents),
        installment_number: li.metadata?.installment_number,
        total_installments: li.metadata?.total_installments,
        total_contract_amount: li.metadata?.total_contract_amount,
      };
    }),
    subtotal: centsToDecimal(invoice.subtotal_cents),
    discount_amount: centsToDecimal(invoice.discount_cents),
    tax_amount: centsToDecimal(invoice.tax_cents),
    total: centsToDecimal(invoice.total_cents),
    memo: invoice.memo,
    payment_instructions: invoice.payment_instructions,
    organization_name: organizationName,
    status: invoice.status,
    portal_url: invoice.portal?.url ? `${window.location.origin}${invoice.portal.url}` : undefined,
    show_portal_link: defaultTemplate?.showPortalLink ?? true,
    branding: storedBranding || templateBranding,
    bank_details: defaultTemplate?.showBankDetails ? {
      bank_name: defaultTemplate.bankName,
      account_name: defaultTemplate.bankAccountName,
      account_number: defaultTemplate.bankAccountNumber,
      routing_number: defaultTemplate.bankRoutingNumber,
      swift_code: defaultTemplate.bankSwiftCode,
    } : undefined,
    accepted_payment_methods: defaultTemplate?.acceptedPaymentMethods,
    payment_method_instructions: defaultTemplate?.paymentMethodInstructions,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{invoice.invoice_number}</h2>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_BADGE_CLASSES[invoice.status] || ''}`}>
                {INVOICE_STATUS_LABELS[invoice.status]}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {invoice.customer_snapshot?.name} {invoice.customer_snapshot?.company ? `- ${invoice.customer_snapshot.company}` : ''}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {invoice.status === 'draft' && (
            <>
              <button onClick={() => onEdit(invoice.id)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                Edit
              </button>
              <button onClick={handleFinalize}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Finalize
              </button>
            </>
          )}
          {['open', 'sent', 'viewed', 'overdue'].includes(invoice.status) && (
            <>
              <button onClick={() => setShowSendModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <Send className="w-3.5 h-3.5" /> Send
              </button>
              <button onClick={() => setShowPaymentForm(!showPaymentForm)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-green-500 text-green-600 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20">
                <DollarSign className="w-3.5 h-3.5" /> Record Payment
              </button>
              <button onClick={handleCopyPortalLink}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                <Link className="w-3.5 h-3.5" /> Portal Link
              </button>
            </>
          )}
          <button onClick={handleDownloadPdf}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
            <Download className="w-3.5 h-3.5" /> PDF
          </button>
          <button onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 print:hidden">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          {onDuplicate && (
            <button onClick={() => onDuplicate(invoice.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              <Copy className="w-3.5 h-3.5" /> Duplicate
            </button>
          )}
          {!['void', 'paid', 'uncollectible'].includes(invoice.status) && (
            <button onClick={handleVoid}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20">
              <XCircle className="w-3.5 h-3.5" /> Void
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
        </div>
      )}

      {/* Payment Form */}
      {showPaymentForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-800 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Record Payment</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Amount ($) *</label>
              <input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                placeholder={centsToDecimal(invoice.amount_due_cents).toFixed(2)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                step="0.01" min="0.01" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Method</label>
              <select value={payMethod} onChange={e => setPayMethod(e.target.value as PaymentMethod)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                {Object.entries(PAYMENT_METHOD_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Reference #</label>
              <input type="text" value={payReference} onChange={e => setPayReference(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Check # / Transaction ID" />
            </div>
            <div className="flex items-end gap-2">
              <button onClick={handleRecordPayment} disabled={payingAction || !payAmount}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {payingAction ? 'Recording...' : 'Record'}
              </button>
              <button onClick={() => setShowPaymentForm(false)}
                className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content: Preview + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoice Preview (2 cols) */}
        <div className="lg:col-span-2 print:col-span-3">
          <InvoicePreview data={previewData} />
        </div>

        {/* Sidebar (1 col) */}
        <div className="space-y-4 print:hidden">
          {/* Amount Summary */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{formatCents(invoice.total_cents)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Paid</span>
                <span className="text-green-600">{formatCents(invoice.amount_paid_cents)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-200 dark:border-gray-600">
                <span className="text-gray-900 dark:text-gray-100">Balance Due</span>
                <span className={invoice.amount_due_cents > 0 ? 'text-red-600' : 'text-green-600'}>
                  {formatCents(invoice.amount_due_cents)}
                </span>
              </div>
            </div>
          </div>

          {/* Email Tracking */}
          {invoice.email_tracking && invoice.email_tracking.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                <Mail className="w-3 h-3 inline mr-1" />Email Activity
              </h3>
              <div className="space-y-3">
                {invoice.email_tracking.map(t => (
                  <div key={t.id} className="text-sm">
                    <div className="flex items-center gap-2">
                      {t.status === 'sent' && <Send className="w-3 h-3 text-blue-500" />}
                      {t.status === 'delivered' && <CheckCircle className="w-3 h-3 text-green-500" />}
                      {t.status === 'opened' && <Eye className="w-3 h-3 text-purple-500" />}
                      {t.status === 'clicked' && <ExternalLink className="w-3 h-3 text-indigo-500" />}
                      {t.status === 'bounced' && <AlertTriangle className="w-3 h-3 text-red-500" />}
                      <span className="text-gray-700 dark:text-gray-300 capitalize">{t.status}</span>
                      {t.open_count > 1 && <span className="text-xs text-gray-400">({t.open_count}x)</span>}
                    </div>
                    <p className="text-xs text-gray-400 ml-5">
                      {t.recipient_email} &middot; {t.sent_at ? fmt.dateTime(t.sent_at) : ''}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment History */}
          {invoice.payments && invoice.payments.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                <DollarSign className="w-3 h-3 inline mr-1" />Payments
              </h3>
              <div className="space-y-2">
                {invoice.payments.map(p => (
                  <div key={p.id} className="flex justify-between text-sm">
                    <div>
                      <p className="text-gray-700 dark:text-gray-300">{formatCents(p.amount_cents)}</p>
                      <p className="text-xs text-gray-400">
                        {p.payment_method} &middot; {fmt.short(p.payment_date)}
                        {p.reference_number && ` &middot; ${p.reference_number}`}
                      </p>
                    </div>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400">
                      {p.source}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reminders */}
          {invoice.reminders && invoice.reminders.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                <Clock className="w-3 h-3 inline mr-1" />Reminders
              </h3>
              <div className="space-y-1.5">
                {invoice.reminders.map(r => (
                  <div key={r.id} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-400">
                      {r.reminder_type.replace(/_/g, ' ')}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded ${
                      r.status === 'sent' ? 'bg-green-50 text-green-600 dark:bg-green-900/20' :
                      r.status === 'pending' ? 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {r.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Customer Portal — always show for non-draft invoices */}
          {invoice.status !== 'draft' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <ExternalLink className="w-3 h-3" />Customer Portal
              </h3>
              {invoice.portal?.url ? (
                <div className="text-sm space-y-3">
                  <div className="flex items-center gap-2 px-2.5 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-700 dark:text-blue-300 break-all">
                    <Link className="w-3 h-3 flex-shrink-0" />
                    <span className="flex-1">{window.location.origin}{invoice.portal.url}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                      <p className="text-gray-400">Views</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{invoice.portal.access_count || 0}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                      <p className="text-gray-400">Last Viewed</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {invoice.portal.last_accessed_at
                          ? fmt.short(invoice.portal.last_accessed_at)
                          : 'Never'}
                      </p>
                    </div>
                  </div>

                  {invoice.portal.expires_at && (
                    <p className="text-[10px] text-gray-400">
                      Expires: {fmt.short(invoice.portal.expires_at)}
                    </p>
                  )}

                  <div className="flex items-center gap-2">
                    <button onClick={handleCopyPortalLink}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                      <Copy className="w-3 h-3" /> Copy Link
                    </button>
                    <a
                      href={`${window.location.origin}${invoice.portal.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <ExternalLink className="w-3 h-3" /> Open Portal
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-sm space-y-2">
                  <p className="text-gray-500 text-xs">
                    Generate a public portal link so your customer can view and pay this invoice online.
                  </p>
                  <button onClick={handleCopyPortalLink}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                    <Link className="w-3 h-3" /> Generate Portal Link
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Send Modal */}
      {showSendModal && (
        <SendInvoiceModal
          organizationId={organizationId}
          invoice={invoice}
          onClose={() => setShowSendModal(false)}
          onSent={handleEmailSent}
        />
      )}
    </div>
  );
}
